import { Skeleton } from "@credit-acceptance/uikit";

import { cn } from "../../../../../lib/utils";
import { formatNoteDatetime } from "../../../../analystNotes/formatNoteDatetime";

// ---------------------------------------------------------------------------
// CallScriptHistoryPanel
//
// The "Notes History" panel shared by both Call Script modals: a loading
// skeleton, an empty state, or the applicant's saved calls — each stamped
// with when it happened and who took it, followed by that call's answers.
//
// Takes every string as a prop rather than calling `t` itself, so neither
// screen's i18n namespace (review.income.* vs review.customer.*) leaks in.
// ---------------------------------------------------------------------------

/** The minimum an answer must expose to be listed. Both scripts' answer types satisfy this. */
export interface CallScriptHistoryPanelAnswer {
  verificationAnswerPk: number;
  questionText: string;
}

/** The minimum an entry must expose. Both scripts' entry types satisfy this. */
export interface CallScriptHistoryPanelEntry<TAnswer extends CallScriptHistoryPanelAnswer> {
  verificationPhoneCallPk: number;
  /** ISO datetime the call occurred. */
  occurredAt: string;
  analystName: string;
  answers: TAnswer[];
}

interface CallScriptHistoryPanelProperties<TAnswer extends CallScriptHistoryPanelAnswer> {
  readonly entries: ReadonlyArray<CallScriptHistoryPanelEntry<TAnswer>>;
  readonly isLoading: boolean;
  readonly title: string;
  readonly loadingLabel: string;
  readonly emptyLabel: string;
  /** The per-entry subheading, e.g. "EMPLOYMENT VERIFICATION - INCOME 1 FOR DRIVER". */
  readonly entryHeader: string;
  /**
   * Renders one answer's value. Employment passes `formatHistoryAnswerValue`
   * (which decodes emp-type codes and formats phones/dates); residence has no
   * such mapping yet and passes the raw entered value.
   */
  readonly renderAnswerValue: (answer: TAnswer) => string;
}

export function CallScriptHistoryPanel<TAnswer extends CallScriptHistoryPanelAnswer>({
  entries,
  isLoading,
  title,
  loadingLabel,
  emptyLabel,
  entryHeader,
  renderAnswerValue,
}: CallScriptHistoryPanelProperties<TAnswer>) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <h3 className="text-primary mb-(--spacing-2) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold)">
        {title}
      </h3>
      {isLoading ? (
        <div role="status" aria-label={loadingLabel} className="px-(--spacing-1) py-(--spacing-3)">
          <Skeleton loading active paragraph={{ rows: 3 }} />
        </div>
      ) : entries.length === 0 ? (
        <p className="text-muted-foreground text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)">
          {emptyLabel}
        </p>
      ) : (
        <div className="bg-muted px-(--spacing-2) py-(--spacing-2) min-h-0 flex-1 overflow-y-auto rounded-lg border">
          {entries.map((entry, entryIndex) => (
            <div
              key={entry.verificationPhoneCallPk}
              className={cn(
                "text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)",
                entryIndex > 0 && "border-border mt-(--spacing-2) pt-(--spacing-2) border-t"
              )}
            >
              <div className="text-foreground font-(--font-weight-semibold)">
                {formatNoteDatetime(entry.occurredAt, { includeSeconds: true })}-{entry.analystName}
              </div>
              <div className="text-muted-foreground">{entryHeader}</div>
              {entry.answers.map((answer) => (
                <div key={answer.verificationAnswerPk}>
                  {answer.questionText} - {renderAnswerValue(answer)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
